import { View, Text, ScrollView, ActivityIndicator, StyleSheet, TouchableOpacity } from "react-native"
import { colorPalette, fontPalette, globalStyles } from "../../static/styles/global"
import HomeDetailsHeader from "../../components/home/HomeDetailsHeader"
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { useshiftstore } from "../../stores/shift"
import { usePersonalStore } from "../../stores/personalInfo"
import { useInitializeStore } from "../../stores/initialize"
import { useHomeStore } from "../../stores/home"
import ScreenStatus from "../../components/home/ScreenStatus"
import { Ionicons } from '@expo/vector-icons'
import moment from "moment"
import Card from "../../components/shared/card/Card"

const PTORequestHistory = ({ navigation, route }: any) => {
    const { t } = useTranslation()
    const shiftStore = useshiftstore()
    const { personalData } = usePersonalStore((store) => ({ personalData: store.personalData }))
    const { initializeData } = useInitializeStore((store) => ({ initializeData: store.initializeData }))
    const { homeData } = useHomeStore((store) => ({ homeData: store.homeData }))

    useEffect(() => {
        if (
            personalData?.entityId &&
            initializeData?.userkey &&
            homeData?.storeNumber
        ) {
            shiftStore.getPTOHistory(
                personalData.entityId,
                initializeData.userkey,
                homeData.storeNumber
            );
            shiftStore.geteccodelist(personalData.entityId);
        }
    }, [personalData?.entityId, initializeData?.userkey, homeData?.storeNumber]);

    const labelfromCode = (code: string) => {
        const label = shiftStore.codelist?.data.find((item) => item.code === code)?.label || "-"
        return label
    }

    const getFormattedDate = (startTimeOff: string, endTimeOff: string) => {
        if (!startTimeOff || !endTimeOff) return "";
        const start = moment(startTimeOff);
        const end = moment(endTimeOff);
        if (!start.isValid() || !end.isValid()) return "";
        if (start.isSame(end, 'day')) {
            return start.format('ddd, MMM DD');
        } else {
            return `${start.format('ddd, MMM DD')} - ${end.format('ddd, MMM DD')}`;
        }
    }

    const getFormattedTime = (startTimeOff: string, endTimeOff: string) => {
        if (!startTimeOff || !endTimeOff) return "";
        const start = moment(startTimeOff);
        const end = moment(endTimeOff);
        if (!start.isValid() || !end.isValid()) return "";
        return `${start.format('h:mm a')} - ${end.format('h:mm a')}`;
    }

    const getStatusDetails = (statusStr: string) => {
        const status = (statusStr || '').toLowerCase();
        if (status.includes('approve') || status.includes('accept')) {
            return {
                label: 'Approved',
                bgColor: '#E6FDF4',
                borderColor: '#9DD086',
                textColor: '#047857',
                icon: 'checkmark-sharp',
            };
        } else if (status.includes('reject') || status.includes('decline')) {
            return {
                label: 'Rejected',
                bgColor: '#FDE8E8',
                borderColor: '#F05252',
                textColor: '#9B1C1C',
                icon: 'close-sharp',
            };
        } else if (status.includes('pending')) {
            return {
                label: 'Pending',
                bgColor: '#E1EFFE',
                borderColor: '#3F83F8',
                textColor: '#1E429F',
                icon: 'hourglass-outline',
            };
        } else {
            return {
                label: statusStr || 'Cancelled',
                bgColor: '#F3F4F6',
                borderColor: '#9CA3AF',
                textColor: '#4B5563',
                icon: 'close-circle-outline',
            };
        }
    }

    const getFilterTabStyles = (item: string, isActive: boolean) => {
        if (!isActive) {
            return {
                backgroundColor: "#F3F4F6",
                textColor: "#4B5563",
                borderColor: "transparent",
            };
        }

        switch (item) {
            case "Approved":
                return {
                    backgroundColor: "#E6FDF4",
                    textColor: "#047857",
                    borderColor: "#10B981",
                };
            case "Rejected":
                return {
                    backgroundColor: "#FDE8E8",
                    textColor: "#9B1C1C",
                    borderColor: "#F05252",
                };
            case "Pending":
                return {
                    backgroundColor: "#4FC7E8 ",
                    textColor: "#1E429F",
                    borderColor: "#3F83F8",
                };
            case "All":
            default:
                return {
                    backgroundColor: "#E2EDF8",
                    textColor: "#1E4E79",
                    borderColor: "#8DB4D7",
                };
        }
    };

    const getIconDetails = (ecCode: string, labelStr: string, statusStr: string) => {
        const code = (ecCode || '').toLowerCase();
        const label = (labelStr || '').toLowerCase();
        const status = (statusStr || '').toLowerCase();

        let iconName = 'calendar-outline';

        // 1. SICK
        if (code.includes('sick') || label.includes('sick') || code.includes('med') || label.includes('med') || code === 'sic') {
            iconName = 'medkit-outline';
        }
        // 2. VACATION / VAC
        else if (code.includes('vac') || label.includes('vacation')) {
            iconName = 'airplane-outline';
        }
        // 3. PERSONAL / PERS
        else if (code.includes('pers') || label.includes('personal') || code === 'per') {
            iconName = 'person-outline';
        }
        // 4. PTO
        else if (code === 'pto' || label.includes('pto')) {
            iconName = 'briefcase-outline';
        }
        // 5. UNPAID / UNPD
        else if (code.includes('unpd') || label.includes('unpaid')) {
            iconName = 'wallet-outline';
        }
        // 6. ON CALL / ONCL
        else if (code.includes('oncl') || label.includes('call') || code.includes('onc')) {
            iconName = 'phone-portrait-outline';
        }
        // 7. MRC
        else if (code.includes('mrc')) {
            iconName = 'flask-outline';
        }
        // 8. General fallback for Vacation/Holiday categories (like custom holidays) that might not be matched above
        else if (label.includes('hol') || label.includes('ann')) {
            iconName = 'airplane-outline';
        }

        let leftBg = '#EFF6FF';
        let iconColor = '#3B82F6';

        if (status.includes('approve') || status.includes('accept')) {
            leftBg = '#E6FDF4';
            iconColor = '#9DD086';
        } else if (status.includes('reject') || status.includes('decline')) {
            leftBg = '#FDE8E8';
            iconColor = '#F05252';
        } else if (status.includes('pending')) {
            leftBg = '#E1EFFE';
            iconColor = '#3F83F8';
        } else {
            leftBg = '#F3F4F6';
            iconColor = '#9CA3AF';
        }

        return { leftBg, iconColor, iconName };
    }

    if (shiftStore.error)
        return <ScreenStatus message={shiftStore.error} />

    const historyData = shiftStore?.ptoHistoryData?.data || [];
    const [selectedStatus, setSelectedStatus] = useState("All");

    const filteredHistoryData = historyData.filter((item: any) => {
        if (selectedStatus === "All") return true;
        const details = getStatusDetails(item.requestStatusDescription);
        return details.label === selectedStatus;
    });

    return (
        <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.scrollContent}>
            {/* Dark Top Band / Header */}
            <View style={[globalStyles.primaryBgColor, globalStyles.containerBand, { paddingBottom: 60 }]}>
                <View style={[globalStyles.horizontalPadGap]}>
                    <HomeDetailsHeader />
                </View>
                <View style={[globalStyles.vCenterItems, { marginBottom: 10 }]}>
                    <Text
                        style={[
                            globalStyles.text,
                            globalStyles.fwBold,
                            globalStyles.fs30,
                            globalStyles.whiteTextColor,
                            globalStyles.textCenter,
                            { fontFamily: fontPalette.primaryBold, marginBottom: 5 }
                        ]}
                    >
                        {t('PTO Request History')}
                    </Text>
                </View>
            </View>

            {/* Content List */}
            <View style={styles.listContainer}>
                <Card>
                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={{
                            // paddingHorizontal: 20,
                            gap: 10,
                            marginTop: 15,
                            marginBottom: 15
                        }}
                    >
                        {["All", "Pending", "Approved", "Rejected"].map((item) => {
                            const tabStyles = getFilterTabStyles(item, selectedStatus === item);
                            return (
                                <TouchableOpacity
                                    key={item}
                                    style={{
                                        paddingHorizontal: 16,
                                        paddingVertical: 8,
                                        borderRadius: 20,
                                        borderWidth: 1,
                                        borderColor: tabStyles.borderColor,
                                        backgroundColor: tabStyles.backgroundColor,
                                    }}
                                    onPress={() => setSelectedStatus(item)}
                                >
                                    <Text
                                        style={{
                                            color: tabStyles.textColor,
                                            fontWeight: "600",
                                            fontFamily: fontPalette.primarySemiBold,
                                            fontSize: 13
                                        }}
                                    >
                                        {t(item)}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>
                    {(shiftStore.loading || shiftStore.ptoHistoryData === null) ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color="#0099E5" />
                        </View>
                    ) : filteredHistoryData.length === 0 ? (
                        <View style={styles.noDataContainer}>
                            <Text style={styles.noDataText}>{t('No PTO request found')}</Text>
                        </View>
                    ) : (
                        filteredHistoryData.map((item: any, index: number) => {
                            const statusDetails = getStatusDetails(item.requestStatusDescription);
                            const dateText = getFormattedDate(item.startTimeOff, item.endTimeOff);
                            const label = labelfromCode(item.ecCode);
                            const typeText = label && label !== '-' ? label : '-';
                            const timeText = getFormattedTime(item.startTimeOff, item.endTimeOff);
                            const isRejected = statusDetails.label === 'Rejected' || (item.requestStatusDescription || '').toLowerCase().includes('reject');
                            const rejectReason = item.rejectReason


                            const { leftBg, iconColor, iconName } = getIconDetails(
                                item.ecCode,
                                typeText,
                                item.requestStatusDescription
                            );

                            return (
                                <View key={index.toString()} style={styles.card}>
                                    {/* Left icon container */}
                                    <View style={[styles.leftIconContainer, { backgroundColor: leftBg }]}>
                                        <Ionicons name={iconName as any} size={24} color={iconColor} />
                                    </View>

                                    {/* Middle block */}
                                    <View style={styles.middleInfo}>
                                        <Text style={styles.dateText}>{dateText}</Text>
                                        {timeText ? <Text style={styles.timeText}>{timeText}</Text> : null}
                                        <Text style={styles.typeText}>{typeText}</Text>
                                        {isRejected && rejectReason ? (
                                            <View style={styles.rejectReasonContainer}>
                                                <Text style={styles.rejectReasonText}>
                                                    {t('Reason')}: {rejectReason}
                                                </Text>
                                            </View>
                                        ) : null}
                                    </View>

                                    {/* Right status badge */}
                                    <View style={styles.rightBadgeContainer}>
                                        <View style={[
                                            styles.statusBadge,
                                            {
                                                backgroundColor: statusDetails.bgColor,
                                                borderColor: statusDetails.borderColor
                                            }
                                        ]}>
                                            <Ionicons
                                                name={statusDetails.icon as any}
                                                size={12}
                                                color={statusDetails.textColor}
                                                style={styles.badgeIcon}
                                            />
                                            <Text style={[styles.badgeText, { color: statusDetails.textColor }]}>
                                                {t(statusDetails.label)}
                                            </Text>
                                        </View>
                                    </View>
                                </View>
                            );
                        })
                    )}
                </Card>
            </View>
        </ScrollView>
    )
}

const styles = StyleSheet.create({
    scrollContainer: {
        backgroundColor: "#FFFFFF",
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    listContainer: {
        marginTop: -44,
        paddingHorizontal: 16,
        paddingBottom: 24,
        flex: 1,
    },
    card: {
        flexDirection: 'row',
        backgroundColor: colorPalette.lighter,
        borderRadius: 12,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 1,
        overflow: 'hidden',
        minHeight: 80,
    },
    leftIconContainer: {
        width: 54,
        alignItems: 'center',
        justifyContent: 'center',
        borderTopLeftRadius: 11,
        borderBottomLeftRadius: 11,
    },
    middleInfo: {
        flex: 1,
        paddingVertical: 12,
        paddingHorizontal: 16,
        justifyContent: 'center',
    },
    dateText: {
        fontSize: 16,
        fontFamily: fontPalette.primaryBold,
        fontWeight: '700',
        color: '#1F2937',
        marginBottom: 2,
    },
    timeText: {
        fontSize: 14,
        color: '#4B5563',
        fontFamily: fontPalette.primarySemiBold,
        fontWeight: '500',
        marginBottom: 2,
    },
    typeText: {
        fontSize: 12,
        color: '#6B7280',
        fontFamily: fontPalette.primarySemiBold,
        fontWeight: '500',
    },
    rejectReasonContainer: {
        marginTop: 2,
        borderRadius: 6,
    },
    rejectReasonText: {
        fontSize: 12,
        fontFamily: fontPalette.primarySemiBold,
        fontWeight: '500',
        color: '#4B5563',
    },
    rightBadgeContainer: {
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        paddingRight: 16,
        paddingTop: 14,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        borderWidth: 1,
    },
    badgeIcon: {
        marginRight: 4,
    },
    badgeText: {
        fontSize: 11,
        fontFamily: fontPalette.primaryBold,
        fontWeight: '700',
    },
    loadingContainer: {
        paddingVertical: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    noDataContainer: {
        paddingVertical: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    noDataText: {
        fontSize: 16,
        color: '#6B7280',
        fontFamily: fontPalette.primaryRegular,
        fontWeight: '500',
    }
})

export default PTORequestHistory
